import { EChartsOption } from 'echarts';
import { ClimateChart } from '../../../model/climate/climate-chart';
import { getSSPColor } from './custom-theme';
import { ClimateChartHelper } from './util/climate-chart-helper';

/** One Region tab chart: a bioclimatic indicator's five levels at the location's zone. */
export interface RegionChartItem {
    title: string;
    fileName: string;
    option: EChartsOption;
    instance?: any;
}

const REGION_LEVEL_COLOR_KEYS = ['SSP1-1.9', 'SSP1-2.6', 'SSP2-4.5', 'SSP3-7.0', 'SSP5-8.5'];

/**
 * L1-L5 take the SSP scenario colours in order, matching the Risk Score charts. Set by level
 * index rather than on the series, so it holds in both views: each bar in the bar view, each
 * section and point of the line in the line view.
 */
const REGION_LEVEL_COLORS = {
    show: false,
    type: 'piecewise',
    dimension: 0,
    pieces: REGION_LEVEL_COLOR_KEYS.map((key, index) => ({
        gt: index === 0 ? undefined : index - 0.5,
        lte: index === REGION_LEVEL_COLOR_KEYS.length - 1 ? undefined : index + 0.5,
        color: getSSPColor(key)
    }))
};

/**
 * In the line view each level is a small coloured circle, like the Risk Score line charts' points,
 * with no line between them: the levels are separate thresholds, not a series over time. Used on
 * the series and on the toolbox's line switch, so it holds whichever view the chart starts in.
 */
const REGION_LINE_VIEW = {
    symbol: 'emptyCircle',
    symbolSize: 4,
    showSymbol: true,
    lineStyle: { opacity: 0 }
};

/**
 * The toolbox's line switch moves the levels onto the ticks (ECharts sets boundaryGap false for a
 * line), so Level 1 would sit on the axes' corner. Puts them back between the ticks, as in the bar view.
 */
export function keepRegionLevelsBetweenTicks(ec: any): void {
    ec.on('magictypechanged', () => ec.setOption({ xAxis: { boundaryGap: true } }));
}

/**
 * The y-axis is sized per chart rather than a fixed 0-5: each indicator has its own unit and
 * range, and some run negative (mean temperature) or fall (SPI-6).
 */
export function toRegionChartItem(chart: ClimateChart): RegionChartItem {
    const title = chart.chartConfig?.yAxis?.labelName || 'Region';
    const fileName = title.replace(/[^a-zA-Z0-9]/g, '_');
    // Each row is the legend, then a value per level; "N/A" levels draw as gaps.
    const rows = Object.values(chart.series || {}).map((row: any[]) => row.map((cell, index) => {
        if (index === 0) {
            return cell;
        }
        const value = Number(cell);
        return cell === null || cell === '' || isNaN(value) ? '-' : value;
    }));
    return {
        title,
        fileName,
        option: {
            animation: false,
            tooltip: { trigger: 'axis' },
            // The region is already in the title, and a single legend swatch cannot show the
            // five level colours.
            legend: { show: false },
            toolbox: regionToolbox(title, fileName),
            grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
            xAxis: { type: 'category', boundaryGap: true },
            // Left-aligned and pulled back over the axis labels: centred on the axis at the
            // chart's left edge, a long unit is clipped.
            yAxis: {
                type: 'value',
                // Not scale: true - the axis keeps 0, so it starts there for all-positive values
                // and just below the lowest value when any is negative.
                // Labelled Score whatever the indicator's own unit.
                name: 'Score',
                nameTextStyle: { align: 'left', padding: [0, 0, 0, -30] }
            },
            visualMap: REGION_LEVEL_COLORS,
            dataset: { source: [chart.xAxis, ...rows] },
            // The dot settings only apply in the line view; a bar ignores them.
            series: rows.map(() => ({
                type: chart.chartConfig?.type || 'bar',
                seriesLayoutBy: 'row',
                ...REGION_LINE_VIEW
            }))
        } as EChartsOption
    };
}

/** The shared climate toolbox, with the line switch drawing dots only. */
function regionToolbox(title: string, fileName: string): any {
    const toolbox: any = ClimateChartHelper.getToolboxConfig(title, fileName);
    const magicType = toolbox?.feature?.magicType;
    if (magicType) {
        magicType.option = { ...(magicType.option || {}), line: REGION_LINE_VIEW };
    }
    return toolbox;
}
